import { useContext } from 'react';
import { Placeholder } from 'react-bootstrap';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import { LimitActionsInputControl } from 'components/LimitInputs/LimitActionsInputControl';
import { hasRole, LIMIT_GROUPS_UPDATE } from 'services/roles';
import { formatDateTime, getLimitInfo, getLimitMessageKey, isCryptoProvider } from 'services/utils';
import Button from 'uikit/Button/Button';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import './CustomerLimitDetailsTableRow.scss';

export const CustomerLimitDetailsTableRow = ({
  isLoading,
  limitIndex,
  limitDetailsData,
  handleChangeLimitGroupStatus,
  handleChangeLimitValue,
  handleDeleteLimit
}) => {
  const i18n = useContext(i18nContext);

  const getLimitLabel = (limit) => {
    const key = 'limit.label.' + getLimitMessageKey(limit);
    let message = i18n.getMessage(key);
    if (message === key) {
      const info = getLimitInfo(limit);
      return Object.keys(info).map((key, index) => (
        <div key={'limit-label-part-' + index}>
          {i18n.getMessage('limit.label.part.' + key)}: <strong>{info[key]}</strong>
        </div>
      ));
    }
    return message;
  };

  const getLimitInfoBlock = (
    limitBlockInfo,
    isLimitInfo = false,
    isLimitValue = false,
    isLimitCreatedAtAndUpdatedAt = false,
    isStatus = false,
    isDeleteLimit = false
  ) => {
    if (isLoading) {
      return (
        <Placeholder as={'p'} animation={'glow'}>
          <Placeholder className={'customer-limit-details-table-loader-placeholder'} xs={12} bg={'secondary'} />
        </Placeholder>
      );
    } else if (isLimitInfo) {
      return getLimitLabel(limitDetailsData);
    } else if (isLimitValue) {
      return (
        <LimitActionsInputControl
          onChange={handleChangeLimitValue}
          currency={limitBlockInfo.currency}
          hold={limitBlockInfo.hold}
          notification={limitBlockInfo.notification}
          reject={limitBlockInfo.reject}
          editable={hasRole(LIMIT_GROUPS_UPDATE)}
          type={limitBlockInfo.transactionType}
          limitType={limitBlockInfo.limitType}
          limitIndex={limitIndex}
          isCryptoProvider={isCryptoProvider(limitBlockInfo.transferProvider)}
        />
      );
    } else if (isLimitCreatedAtAndUpdatedAt) {
      return (
        <div className={'customer-limit-details-table-date-and-time'}>
          Created:
          <br /> {formatDateTime(limitDetailsData.createdAt)} <br />
          <br />
          Updated:
          <br /> {formatDateTime(limitDetailsData.updatedAt)}
        </div>
      );
    } else if (isStatus) {
      return (
        <div className={'customer-limit-details-table-status-wrapper'}>
          <CheckBox
            className={'customer-limit-details-table-status-checkbox'}
            name={'hidden'}
            isActive={limitBlockInfo}
            isReverseView={true}
            showCheckBox={true}
            onChange={() => handleChangeLimitGroupStatus(limitIndex, !limitBlockInfo)}
            value={i18n.getMessage('limits.details.table.row.enabled')}
            disabled={isLoading || !hasRole(LIMIT_GROUPS_UPDATE)}
          />
        </div>
      );
    } else if (isDeleteLimit) {
      return (
        <Button
          onClick={() => handleDeleteLimit(limitDetailsData.id)}
          type={'primary'}
          size={'small'}
          isDisabled={!limitDetailsData.individualLimit || isLoading || !hasRole(LIMIT_GROUPS_UPDATE)}
        >
          {i18n.getMessage('limits.details.table.row.delete')}
        </Button>
      );
    } else {
      return <p>{limitBlockInfo}</p>;
    }
  };

  return (
    <tr
      className={
        limitDetailsData?.individualLimit
          ? 'customer-limit-details-table-row-individual'
          : limitDetailsData?.isUpdatedLimit
            ? 'customer-limit-details-table-row-updated'
            : ''
      }
    >
      <td className={'customer-limit-details-table-cell'}>{getLimitInfoBlock(limitDetailsData, true)}</td>
      <td className={'customer-limit-details-table-cell'}>{getLimitInfoBlock(limitDetailsData, false, true)}</td>
      <td className={'customer-limit-details-table-cell'}>
        {getLimitInfoBlock(
          limitDetailsData?.individualLimit ? (
            <i>{i18n.getMessage('limits.details.table.row.isIndividual')}</i>
          ) : limitDetailsData?.isUpdatedLimit ? (
            <i>{i18n.getMessage('limits.details.table.row.isUpdated')}</i>
          ) : (
            limitDetailsData.limitGroupId
          )
        )}
      </td>
      <td className={'customer-limit-details-table-cell'}>{getLimitInfoBlock(limitDetailsData, false, false, true)}</td>
      <td className={'customer-limit-details-table-cell'}>
        {getLimitInfoBlock(limitDetailsData.enabled, false, false, false, true)}
      </td>
      <td className={'customer-limit-details-table-cell'}>
        {limitDetailsData.individualLimit && getLimitInfoBlock(limitDetailsData, false, false, false, false, true)}
      </td>
    </tr>
  );
};

CustomerLimitDetailsTableRow.propTypes = {
  isLoading: PropTypes.bool,
  limitIndex: PropTypes.number,
  handleChangeLimitGroupStatus: PropTypes.func,
  handleChangeLimitValue: PropTypes.func,
  handleDeleteLimit: PropTypes.func,
  limitDetailsData: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.number,
      limitGroupId: PropTypes.string,
      limitType: PropTypes.string,
      period: PropTypes.string,
      transactionType: PropTypes.string,
      transferProvider: PropTypes.string,
      transferType: PropTypes.string,
      currency: PropTypes.string,
      network: PropTypes.string,
      reject: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      hold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      notification: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
      enabled: PropTypes.bool,
      individualLimit: PropTypes.bool
    }),
    PropTypes.oneOf([null])
  ])
};
